import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ScanLine, Search, MapPin } from "lucide-react";

export default function BuyerDashboard() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [manualId, setManualId] = useState("");
  const nav = useNavigate();
  const scannerRef = useRef(null);
  const readerId = "gudlink-qr-reader";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/buyer/scans");
        setItems(data.items);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    if (!scanOpen) {
      if (scannerRef.current) { scannerRef.current.stop().catch(()=>{}); scannerRef.current = null; }
      return;
    }
    const el = document.getElementById(readerId);
    if (!el) return;
    const scanner = new Html5Qrcode(readerId);
    scannerRef.current = scanner;
    scanner.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: 240 },
      (decoded) => {
        try {
          const url = new URL(decoded);
          const parts = url.pathname.split("/");
          const id = parts[parts.length - 1];
          if (id) { setScanOpen(false); nav(`/verify/${id}`); return; }
        } catch {
          // Might be raw id
          setScanOpen(false); nav(`/verify/${decoded}`);
        }
      },
      () => { /* ignore per-frame errors */ }
    ).catch(err => toast.error("Camera error: " + err));
    return () => { scanner.stop().catch(()=>{}); scannerRef.current = null; };
  }, [scanOpen, nav]);

  const filtered = items.filter(b =>
    !q || b.batch_code.toLowerCase().includes(q.toLowerCase())
    || (b.fpo_org || "").toLowerCase().includes(q.toLowerCase())
    || b.source_location.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-secondary">Buyer Dashboard</div>
          <h1 className="font-marketing text-4xl sm:text-5xl mt-2">Scan first. Sign later.</h1>
          <p className="text-muted-foreground mt-1">Verify any Gudlink batch before you commit.</p>
        </div>
        <Dialog open={scanOpen} onOpenChange={setScanOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="scan-qr-btn"><ScanLine className="h-4 w-4 mr-2"/>Scan QR</Button>
          </DialogTrigger>
          <DialogContent data-testid="scan-dialog">
            <DialogHeader><DialogTitle className="font-heading">Scan a batch QR</DialogTitle></DialogHeader>
            <div id={readerId} className="w-full aspect-square bg-black rounded-xl overflow-hidden"/>
            <div className="pt-2 text-sm text-muted-foreground">Or paste the verification URL / batch ID:</div>
            <div className="flex gap-2">
              <Input placeholder="Batch ID or verification URL" value={manualId} onChange={e=>setManualId(e.target.value)} data-testid="manual-id-input"/>
              <Button onClick={() => {
                const v = manualId.trim(); if (!v) return;
                try {
                  const url = new URL(v);
                  const parts = url.pathname.split("/");
                  nav(`/verify/${parts[parts.length-1]}`);
                } catch { nav(`/verify/${v}`); }
                setScanOpen(false);
              }} data-testid="manual-verify-btn">Verify</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 rounded-2xl bg-card border border-border p-4 flex items-center gap-3">
        <Search className="h-4 w-4 text-muted-foreground"/>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search verified batches by code, FPO, or location…" className="flex-1 bg-transparent outline-none text-sm" data-testid="search-input"/>
      </div>

      <h2 className="font-heading text-xl font-semibold mt-10">Recently verified batches</h2>
      <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="verified-grid">
        {filtered.length === 0 ? (
          <div className="text-muted-foreground text-sm">No verified batches yet.</div>
        ) : filtered.map(b => (
          <button key={b.id} onClick={()=>nav(`/verify/${b.id}`)} className="text-left rounded-2xl bg-card border border-border p-5 hover:border-primary transition-colors" data-testid={`verified-card-${b.batch_code}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-secondary">{b.product_type}</div>
                <div className="font-heading text-lg font-semibold mt-1">{b.batch_code}</div>
              </div>
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 capitalize">verified</Badge>
            </div>
            <div className="mt-2 text-sm text-muted-foreground">{b.fpo_org}</div>
            <div className="mt-1 text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5"/>{b.source_location}</div>
            <div className="mt-3 text-sm"><b>{b.quantity_kg} kg</b> · {b.scans_count} scans</div>
          </button>
        ))}
      </div>
    </div>
  );
}
